import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { connectionsQueryKey } from '@/features/integrations/useConnections'
import { fetchToolPermissions, saveToolPermissions } from '@/services/integrations/toolPermissions'
import { permissionGroups } from './permissionGroups.js'

export function useToolPermissionsEditor({ connectionId, onClose }) {
  const cache = useQueryClient()
  const [changeState, setChangeState] = useState({ connectionId, values: {} })
  if (changeState.connectionId !== connectionId) setChangeState({ connectionId, values: {} })
  const query = useQuery({ queryKey: ['integration-permissions', connectionId], queryFn: () => fetchToolPermissions({ connectionId }), enabled: Boolean(connectionId) })
  const groups = useMemo(() => permissionGroups(query.data?.groups, changeState.values), [query.data, changeState.values])
  const mutation = useMutation({
    mutationFn: (tools) => saveToolPermissions({ connectionId, expectedRevision: query.data.revision, tools }),
    onSuccess: () => { cache.invalidateQueries({ queryKey: connectionsQueryKey() }); onClose() },
  })
  const setTier = useCallback((slug, tier) => setChangeState((current) => ({ ...current, values: { ...current.values, [slug]: tier } })), [])
  const cancel = useCallback(() => { setChangeState({ connectionId: null, values: {} }); onClose() }, [onClose])
  const save = useCallback(() => {
    const tools = groups.flatMap((group) => group.tools.map((tool) => ({ slug: tool.slug, tier: tool.tier })))
    mutation.mutate(tools)
  }, [groups, mutation])
  return { data: query.data, groups, loading: query.isLoading, error: query.error, saving: mutation.isPending, setTier, save, cancel }
}
